import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { V3_APP_BASE_URL } from '@/constants/Config';
import type { IOrderDetailed, IOrderItem } from '@/common/interfaces/order.interface';
import { removeDuplicates, sortByProperty } from '@/utilities/CommonUtils';
import { mostRecentActivityStatusMap } from '@/modules/MostRecent/MostRecentActivity';
import type { IInvoiceData, IInvoiceObject } from '@/common/interfaces/invoice.interface';

interface IOrderDetailsResp {
  order: IOrderDetailed;
}

interface IErrRes {
  status: string | number;
  data: { message: string };
}

interface IPdfData {
  pdfLink: string;
}

interface IInvoiceSectionResp {
  invoices: IInvoiceObject[];
  count: number;
}

type IRawOrderItem = IOrderItem & { fundraiser_amount?: string | number | null };

type IRawOrderDetailed = IOrderDetailed & {
  charity_name?: string | null;
  store_type?: string | null;
  permanent_store_closed_at?: string | null;
};

const normalizeOrderItem = (orderItem: IRawOrderItem): IOrderItem => {
  const rawFundraiserAmount = orderItem.fundraiserAmount ?? orderItem.fundraiser_amount;
  const parsedFundraiserAmount =
    rawFundraiserAmount != null && rawFundraiserAmount !== ''
      ? Number(rawFundraiserAmount)
      : undefined;

  return {
    ...orderItem,
    fundraiserAmount:
      parsedFundraiserAmount != null && !Number.isNaN(parsedFundraiserAmount)
        ? parsedFundraiserAmount
        : undefined,
  };
};

const normalizeOrderDetailed = (order: IRawOrderDetailed): IOrderDetailed => {
  const charityName = String(order.charityName ?? order.charity_name ?? '').trim();
  const storeType = String(order.storeType ?? order.store_type ?? '').trim();
  const normalizedStoreType = storeType.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
  const permanentStoreClosedAt = String(
    order.permanentStoreClosedAt ?? order.permanent_store_closed_at ?? ''
  ).trim();

  return {
    ...order,
    charityName: charityName || undefined,
    storeType: normalizedStoreType || undefined,
    permanentStoreClosedAt: permanentStoreClosedAt || undefined,
    orderItems: (order.orderItems ?? []).map((orderItem) =>
      normalizeOrderItem(orderItem as IRawOrderItem)
    ),
  };
};

export const transformGetOrderByIdResponse = (response: IOrderDetailsResp): IOrderDetailed => {
  if (response.order && response.order.orderItems?.length > 0) {
    const sortedOrderItems = sortByProperty<IRawOrderItem>(
      response.order.orderItems as IRawOrderItem[],
      'orderItemId'
    ).map(normalizeOrderItem);

    const normalizedOrder = normalizeOrderDetailed({
      ...response.order,
      orderItems: sortedOrderItems,
      status: mostRecentActivityStatusMap[response.order.status],
    });

    return normalizedOrder;
  }

  return normalizeOrderDetailed({
    ...response.order,
    orderItems: [],
    status: mostRecentActivityStatusMap[response.order.status],
  });
};

/** Defining a service using a base URL and expected endpoints.
 * This API will be used to validate Invoices and Orders. As invoices and orders are interlinked, we will need to update the cache based on the tags.
 */
export const orderInvoiceApi = createApi({
  reducerPath: 'orderInvoiceApi',
  tagTypes: ['Payment'],
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (build) => ({
    getOrderById: build.query({
      query: (orderId) => ({
        url: `api/cp/order/${orderId}`,
        method: 'get',
      }),
      /** Sort order items by order item id  */
      transformResponse: transformGetOrderByIdResponse,
      providesTags: ['Payment'],
    }),

    postPayment: build.mutation({
      query: ({ data }) => ({
        url: `api/v4/payment`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { payment: IInvoiceData }) => ({
        data: response.payment,
        status: 'success',
      }),
      transformErrorResponse: (response: { error: IErrRes }) => response,
      invalidatesTags: ['Payment'],
    }),
    downloadInvoicePdf: build.query({
      query: ({ id, params }) => ({ url: `api/v4/invoice/${id}`, params, method: 'get' }),
      transformResponse: (response: IPdfData | { data?: IPdfData; pdfLink?: string }) => {
        if (response && typeof response === 'object' && 'pdfLink' in response && response.pdfLink) {
          return { pdfLink: response.pdfLink };
        }

        if (
          response &&
          typeof response === 'object' &&
          'data' in response &&
          response.data?.pdfLink
        ) {
          return { pdfLink: response.data.pdfLink };
        }

        return response as IPdfData;
      },
      transformErrorResponse: (response: { error: IErrRes }) => response,
      providesTags: ['Payment'],
    }),
    getInvoice: build.query({
      query: ({ id, publicAccess }: { id: string; publicAccess: boolean }) => ({
        url: `api/cp/invoice/${id}`,
        method: 'get',
        params: { publicAccess },
      }),
      transformResponse: (response: IInvoiceObject) => response,
      providesTags: ['Payment'],
    }),
    getInvoices: build.query({
      query: ({ limit, page }) => ({
        url: `api/cp/invoice?limit=${limit}&page=${page}`,
        method: 'get',
      }),
      transformResponse: (response: IInvoiceSectionResp) => ({
        invoices: response.invoices,
        count: response.count,
      }),
      providesTags: ['Payment'],
      /** Only have one cache entry because the arg always maps to one string */
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.invoices.push(...newItems.invoices);
        currentCache.invoices = removeDuplicates(currentCache.invoices, 'invoiceId');
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    postInvoicePayment: build.mutation({
      query: ({ data, publicAccess }: { data: unknown; publicAccess: boolean }) => ({
        url: `api/cp/payment`,
        method: 'POST',
        data,
        params: { publicAccess },
      }),
      transformResponse: (response: { payment: IInvoiceData }) => ({
        data: response.payment,
        status: 'success',
      }),
      transformErrorResponse: (response: { error: IErrRes }) => response,
      invalidatesTags: ['Payment'],
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const {
  useGetOrderByIdQuery,
  usePostPaymentMutation,
  useLazyDownloadInvoicePdfQuery,
  useGetInvoiceQuery,
  useGetInvoicesQuery,
  usePostInvoicePaymentMutation,
} = orderInvoiceApi;
